import { useEffect, useState } from "react";

function buildUrl(apiKey, libraries = [], language, region, version) {
  const params = new URLSearchParams();
  if (apiKey) params.set("key", apiKey);
  if (libraries && libraries.length) params.set("libraries", libraries.join(","));
  if (language) params.set("language", language);
  if (region) params.set("region", region);
  if (version) params.set("v", version);
  return `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
}

export default function useLoadGoogleMaps({ apiKey, libraries = [], language, region, version, id = "google-maps-script" } = {}) {
  const [loaded, setLoaded] = useState(() => !!(typeof window !== "undefined" && window.google && window.google.maps));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (loaded) return;
    const existing = document.getElementById(id);
    if (existing) {
      // If another instance is loading the script, attach listeners
      const onLoad = () => setLoaded(true);
      const onError = (e) => setError(new Error("Failed to load Google Maps script"));
      existing.addEventListener("load", onLoad);
      existing.addEventListener("error", onError);
      return () => {
        existing.removeEventListener("load", onLoad);
        existing.removeEventListener("error", onError);
      };
    }

    const script = document.createElement("script");
    script.id = id;
    script.src = buildUrl(apiKey, libraries, language, region, version);
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";

    const handleLoad = () => {
      setLoaded(true);
    };
    const handleError = (ev) => {
      const msg = `Google Maps script failed to load (${ev?.type || "error"})`;
      console.error(msg, ev);
      setError(new Error(msg));
    };

    // Google Maps specific auth failure callback
    const prevAuthFail = window.gm_authFailure;
    window.gm_authFailure = function () {
      const msg = "Google Maps authentication failure (gm_authFailure)";
      console.error(msg);
      setError(new Error(msg));
      if (typeof prevAuthFail === "function") prevAuthFail();
    };

    script.addEventListener("load", handleLoad);
    script.addEventListener("error", handleError);
    document.head.appendChild(script);

    return () => {
      script.removeEventListener("load", handleLoad);
      script.removeEventListener("error", handleError);
      // Do not remove the script tag on cleanup; other components may use it
      window.gm_authFailure = prevAuthFail;
    };
  }, [apiKey, id, libraries, language, region, version, loaded]);

  return { loaded, error };
}
